import { supabase } from '../lib/supabase';

export interface DocumentInfo {
  id: string;
  user_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  document_type: string;
  description?: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  url?: string; // Signed URL for accessing the file
}

export class DocumentService {
  /**
   * Fetch documents by their UUIDs from the documents table
   */
  static async getDocumentsByIds(documentIds: string[]): Promise<DocumentInfo[]> {
    if (!documentIds || documentIds.length === 0) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .in('id', documentIds);

      if (error) {
        console.error('Error fetching documents by IDs:', error);
        throw error;
      }

      if (!data) {
        return [];
      }

      // Generate signed URLs for each document
      const documentsWithUrls = await Promise.all(
        data.map(async (doc) => {
          try {
            const { data: urlData, error: urlError } = await supabase.storage
              .from('user-documents')
              .createSignedUrl(doc.file_path, 3600); // 1 hour expiry

            if (urlError) {
              console.error('Error creating signed URL for document:', doc.id, urlError);
              return {
                ...doc,
                url: null
              };
            }

            return {
              ...doc,
              url: urlData?.signedUrl || null
            };
          } catch (urlError) {
            console.error('Error creating signed URL for document:', doc.id, urlError);
            return {
              ...doc,
              url: null
            };
          }
        })
      );

      return documentsWithUrls.filter(doc => doc.url !== null);
    } catch (error) {
      console.error('Error in getDocumentsByIds:', error);
      throw error;
    }
  }

  /**
   * Get a single document by ID
   */
  static async getDocumentById(documentId: string): Promise<DocumentInfo | null> {
    const documents = await this.getDocumentsByIds([documentId]);
    return documents.length > 0 ? documents[0] : null;
  }

  /**
   * Get public URL for a document (if it's public)
   */
  static async getPublicUrl(filePath: string): Promise<string | null> {
    try {
      const { data } = supabase.storage
        .from('user-documents')
        .getPublicUrl(filePath);

      return data?.publicUrl || null;
    } catch (error) {
      console.error('Error getting public URL:', error);
      return null;
    }
  }

  /**
   * Create a signed URL for a document
   */
  static async createSignedUrl(filePath: string, expiresIn: number = 3600): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from('user-documents')
        .createSignedUrl(filePath, expiresIn);

      if (error) {
        console.error('Error creating signed URL:', error);
        return null;
      }

      return data?.signedUrl || null;
    } catch (error) {
      console.error('Error creating signed URL:', error);
      return null;
    }
  }
}

